import { useTranslation } from "react-i18next";
import { useCmsContent } from "@/hooks/use-cms-content";

/**
 * Returns `c(cmsKey, i18nKey, fallback?)`.
 *
 * Lookup order:
 * 1. CMS value for `<cmsKey>__<locale>` (locale-specific override)
 * 2. For pt-PT: CMS value for `<cmsKey>` (base = pt-PT source of truth)
 * 3. i18n translation for `i18nKey`
 * 4. Base CMS value `<cmsKey>` (fallback for non-PT if no translation)
 * 5. `fallback` argument
 */
export const useLocalizedContent = () => {
  const { content, loading } = useCmsContent();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-PT";
  const isPt = locale === "pt-PT" || locale === "pt";

  const c = (cmsKey: string, i18nKey: string, fallback?: string): string => {
    // 1) locale-specific override
    const localeKey = `${cmsKey}__${locale}`;
    if (content[localeKey]) return content[localeKey];

    // 2) pt-PT uses the base key directly
    if (isPt && content[cmsKey]) return content[cmsKey];

    // 3) i18n translation
    const translated = t(i18nKey);
    if (translated && translated !== i18nKey) return translated;

    // 4) base CMS fallback
    if (content[cmsKey]) return content[cmsKey];

    // 5) explicit fallback
    return fallback || translated;
  };

  return { c, content, loading, isPt, locale };
};
